import { useState } from "react";

/**
 * MPPTextArea - a reusable uncontrolled textarea that matches GoA design
 * system styling without using the GoabTextarea web component (which causes
 * a disappearing-text bug).
 *
 * Props:
 *   name        - string, field name
 *   defaultValue - string, initial value
 *   maxLength   - number, optional. Shows a character counter when provided.
 *   rows        - number, default 3
 *   error       - string | falsy, error message to drive border/shadow colour
 *   disabled    - bool
 *   width       - CSS width string, default '100%'
 *   maxWidth    - CSS max-width string, default '60ch'
 *   onChange    - ({ name, value }) => void
 *   onBlur      - () => void, optional
 */
const MPPTextArea = ({
  name,
  defaultValue = '',
  maxLength,
  rows = 3,
  error,
  disabled,
  width = '100%',
  maxWidth = '60ch',
  onChange,
  onBlur,
}) => {
  const [charCount, setCharCount] = useState((defaultValue ?? '').length);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const borderColor = error
    ? 'var(--goa-color-interactive-error)'
    : 'var(--goa-color-greyscale-black, #000)';

  let boxShadow = 'none';
  if (error)          boxShadow = 'inset 0 0 0 1px var(--goa-color-interactive-error)';
  else if (isFocused) boxShadow = '0 0 0 3px var(--goa-color-interactive-focus, #feba35)';
  else if (isHovered) boxShadow = 'inset 0 0 0 1px var(--goa-color-greyscale-black, #000)';

  return (
    <div style={{ position: 'relative', width, maxWidth }}>
      <textarea
        id={name}
        name={name}
        defaultValue={defaultValue}
        rows={rows}
        disabled={disabled}
        style={{
          width: '100%',
          minHeight: `calc(${rows} * 1.5em + 1.25rem)`,
          padding: '0.5rem 0.75rem',
          paddingBottom: maxLength ? '2.25rem' : '0.5rem',
          fontSize: 'var(--goa-font-size-4, 1rem)',
          fontFamily: 'var(--goa-font-family-sans, inherit)',
          lineHeight: '1.5',
          border: `1px solid ${borderColor}`,
          boxShadow,
          outline: 'none',
          borderRadius: 'var(--goa-border-radius-m, 4px)',
          boxSizing: 'border-box',
          resize: 'none',
          backgroundColor: disabled ? 'var(--goa-color-greyscale-100, #f1f1f1)' : '#fff',
          color: 'var(--goa-color-text-default, #333)',
        }}
        onChange={(e) => {
          if (maxLength) setCharCount(e.target.value.length);
          if (onChange) onChange({ name, value: e.target.value });
          e.target.scrollTop = e.target.scrollHeight;
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => {
          setIsFocused(false);
          if (onBlur) onBlur();
        }}
        aria-invalid={!!error}
      />
      {maxLength && (() => {
        const remaining = maxLength - charCount;
        const isOver = remaining < 0;
        return (
          <span style={{
            position: 'absolute',
            bottom: '0.8rem',
            right: '1.1rem',
            fontSize: 'var(--goa-font-size-2, 0.875rem)',
            color: isOver ? 'var(--goa-color-interactive-error)' : 'var(--goa-color-text-primary)',
            pointerEvents: 'none',
          }}>
            {isOver
              ? `${Math.abs(remaining)} characters too many`
              : `${remaining} characters remaining`
            }
          </span>
        );
      })()}
    </div>
  );
};

export default MPPTextArea;
